
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Stars,
  Moon,
  Luggage,
  Download,
  MapPin,
  Clock,
  Target,
  Check,
  Crown,
  ArrowRight,
  Camera
} from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { CalendarPreviewSlice } from '@/components/calendar/CalendarPreviewSlice';
import { HERO_PREMIUM_COPY } from '@/components/heroPremiumCopy';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout } from '@/lib/analytics';
import { toast } from 'sonner';

interface FeaturesProps {
  onShowAuthModal: () => void;
}

const planners = [
  {
    title: 'Milky Way Planner',
    icon: Stars,
    description: 'Plan perfect nights for capturing the galactic core with precision timing and visibility data.',
    features: [
      'Galactic core rise & set times with compass directions',
      'Monthly visibility calendar with 1–10 ratings',
      'Best nights analysis for optimal shooting',
      'Dark sky window calculations',
      'Astronomical twilight tracking',
      'Export to Excel, CSV, or Calendar'
    ]
  },
  {
    title: 'Moon Planner',
    icon: Moon,
    description: 'Track lunar cycles, supermoons, and perfect timing for dramatic moon photography.',
    features: [
      'Moon rise & set times with directional data',
      'Supermoon tracking throughout the year',
      'Golden hour & blue hour overlap detection',
      'Best full moons recommended monthly',
      'Phase tracking with illumination percentages',
      'Export to Excel, CSV, or Calendar'
    ]
  },
  {
    title: 'Trip Planner',
    icon: Luggage,
    description: 'Build multi-stop photography itineraries with date-specific visibility for each location.',
    features: [
      'Multi-stop itinerary building',
      'Date-specific visibility for each stop',
      'Add locations from favorites or custom search',
      'Vertical timeline view for trip overview',
      'Full trip export to calendar or spreadsheet',
      'Plan weeks or months of travel in one view'
    ]
  }
];

const extras = [
  { icon: MapPin, label: 'Unlimited saved locations' },
  { icon: Download, label: 'Excel, CSV & ICS exports' },
  { icon: Target, label: '1–10 visibility ratings' },
  { icon: Clock, label: 'Precise rise/set timing' },
];

const Features: React.FC<FeaturesProps> = ({ onShowAuthModal }) => {
  const { user, isSubscribed } = useAuth();
  const navigate = useNavigate();

  const handlePremiumUpgrade = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          returnUrl: `${window.location.origin}/dashboard?welcome=true`,
          referralId: getRewardfulReferral()
        }
      });

      if (error) throw error;
      if (data?.url) {
        redirectToCheckout(data.url, 'Premium');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Unable to start checkout. Please try again.');
    }
  };

  const ctaAction = user && isSubscribed
    ? null
    : user
      ? handlePremiumUpgrade
      : () => navigate('/register');

  return (
    <>
      <SEOHead
        title="Astrophotography Planners — Milky Way, Moon, and Trips"
        description="Milky Way visibility calendar, moon phase planner with supermoons, and a trip planner with weather. Every location and export, $25/year."
        keywords="astrophotography planner, milky way planner features, moon planner, trip planner astrophotography, night sky photography tools, astrophotography planning app, milky way visibility tool, best astrophotography app"
        canonicalUrl="/features"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Milky Way Planner",
          "applicationCategory": "PhotographyApplication",
          "operatingSystem": "Web Browser",
          "description": "Complete astrophotography planning suite with Milky Way Planner, Moon Planner, and Trip Planner.",
          "url": "https://www.milkywayplanner.com/features",
          "offers": {
            "@type": "Offer",
            "price": "25",
            "priceCurrency": "USD",
            "billingIncrement": "P1Y"
          },
          "featureList": [
            "Galactic core rise & set times",
            "Moon rise & set times with supermoon tracking",
            "Multi-stop trip itinerary building",
            "Export to Excel, CSV, or Calendar",
            "Unlimited saved locations",
            "Visibility ratings 1-10"
          ]
        }}
      />

      <div className="min-h-screen bg-background">

        {/* Hero */}
        <section className="bg-slate-950 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Premium</span>
            <h1 className="text-4xl md:text-5xl text-white mb-5 tracking-[-0.03em]">
              Three planners.<br />One subscription.
            </h1>
            <p className="text-lg text-white/70 max-w-xl mx-auto leading-relaxed mb-10">
              Everything an astrophotographer needs to plan Milky Way sessions, moon shoots, and multi-stop trips — for less than a coffee per month.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-white font-serif tracking-tight">$25</span>
                <span className="text-white/60">/year</span>
                <span className="text-sm text-white/40 ml-1">($2.08/mo)</span>
              </div>
              {ctaAction ? (
                <Button variant="premium" onClick={ctaAction} size="lg" className="px-8">
                  <Crown className="w-4 h-4 mr-2" />
                  Get Premium
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-400">
                  <Check className="w-4 h-4" />
                  You have Premium Access
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Three planners — feat-grid */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="feat-grid">
            {planners.map((planner, i) => {
              const Icon = planner.icon;
              const isDark = i === 0;
              return (
                <div
                  key={planner.title}
                  className={`p-8 sm:p-10 flex flex-col min-w-0 ${isDark ? 'bg-navy' : 'bg-white'}`}
                >
                  <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-5 ${
                    isDark ? 'bg-white/10' : 'bg-muted'
                  }`}>
                    <Icon className={`w-5 h-5 ${isDark ? 'text-gold-light' : 'text-gold'}`} />
                  </div>

                  <h2 className={`text-xl font-sans font-semibold mb-2 ${isDark ? 'text-white' : 'text-foreground'}`}>
                    {planner.title}
                  </h2>
                  <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-white/75' : 'text-muted-foreground'}`}>
                    {planner.description}
                  </p>

                  {isDark && (
                    <div className="hidden lg:block mb-6 min-w-0">
                      <p className="text-xs font-medium text-white/75 mb-2">{HERO_PREMIUM_COPY.caption}</p>
                      <CalendarPreviewSlice showPremiumRow={false} maxRows={3} />
                    </div>
                  )}

                  {/* Top-aligned. mt-auto opened a font-dependent band under the taller calendar card. */}
                  <ul className="space-y-2.5">
                    {planner.features.map((feat) => (
                      <li key={feat} className={`flex items-start gap-2.5 text-sm ${isDark ? 'text-white/80' : 'text-foreground'}`}>
                        <span className={`mt-0.5 shrink-0 ${isDark ? 'text-gold-light' : 'text-gold'}`}>✦</span>
                        {feat}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>

        {/* Product screenshots */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <span className="eyebrow text-gold-foreground mb-3 block text-center">See It in Action</span>
          <h2 className="text-2xl sm:text-3xl text-foreground tracking-[-0.03em] text-center mb-12">
            What you'll actually use
          </h2>

          <div className="space-y-16">
            {/* Milky Way Calendar */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="overflow-hidden rounded-[4px] border border-border shadow-lg">
                <img
                  src="/lovable-uploads/screenshot-milkyway-calendar.webp"
                  alt="Milky Way visibility calendar showing nightly ratings, galactic core timing, and moon phases for Moab, Utah"
                  className="w-full"
                  loading="lazy"
                />
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Stars className="w-5 h-5 text-gold" />
                  <h3 className="text-xl font-semibold text-foreground">Milky Way Calendar</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Every night rated 0–10 for your exact location. Galactic core rise and set times, moon phase percentage, and dark sky windows — all in one view.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Nightly visibility ratings</li>
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Galactic core timing</li>
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Export to PDF, Excel, or ICS</li>
                </ul>
              </div>
            </div>

            {/* Moon Planner */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="order-1 md:order-2 overflow-hidden rounded-[4px] border border-border shadow-lg">
                <img
                  src="/lovable-uploads/screenshot-moon-planner.webp"
                  alt="Moon planner showing moonrise and moonset times, compass directions, and golden hour overlap for Jasper, Canada"
                  className="w-full"
                  loading="lazy"
                />
              </div>
              <div className="order-2 md:order-1 space-y-4">
                <div className="flex items-center gap-2">
                  <Moon className="w-5 h-5 text-gold" />
                  <h3 className="text-xl font-semibold text-foreground">Moon Planner</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Moonrise and moonset with compass directions, supermoon dates, and golden hour overlaps. Plan dramatic lunar compositions with precision.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Compass direction for rise/set</li>
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Supermoon tracking</li>
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Golden & blue hour overlap</li>
                </ul>
              </div>
            </div>

            {/* Trip Planner */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="overflow-hidden rounded-[4px] border border-border shadow-lg">
                <img
                  src="/lovable-uploads/screenshot-trip-planner.webp"
                  alt="Trip planner showing multi-stop itinerary with map, dates, and visibility ratings for a Utah photography trip"
                  className="w-full"
                  loading="lazy"
                />
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Luggage className="w-5 h-5 text-gold" />
                  <h3 className="text-xl font-semibold text-foreground">Trip Planner</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Build multi-stop photography itineraries with a map view and date-specific visibility for each location along the way.
                </p>
                <ul className="text-sm text-muted-foreground space-y-1.5">
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Multi-stop itineraries</li>
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Map view with all stops</li>
                  <li className="flex items-start gap-2"><span className="text-gold mt-0.5">✦</span> Export full trip to calendar</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Also included — horizontal strip */}
        <section className="section-alt py-14">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <span className="eyebrow text-gold-foreground mb-3 block text-center">Also Included</span>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-8">
              {extras.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.label} className="text-center">
                    <Icon className="w-6 h-6 mx-auto mb-3 text-gold" />
                    <p className="text-sm text-foreground font-medium">{item.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Workshops */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="grid md:grid-cols-2 gap-6">
            {/* For photographers */}
            <Link to="/workshops" className="group p-8 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-gold" />
                </div>
                <h3 className="text-lg font-semibold text-white">Astrophotography Workshops</h3>
              </div>
              <p className="text-sm text-white/60 leading-relaxed mb-4">
                Browse workshops at dark sky locations worldwide. Learn from experienced photographers with hands-on instruction in the field.
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1.5">
                Browse Workshops <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>

            {/* For workshop leaders */}
            <Link to="/workshops/list-your-workshop" className="group p-8 rounded-xl border border-border hover:border-gold/30 hover:bg-gold/5 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-lg bg-muted flex items-center justify-center">
                  <Crown className="w-5 h-5 text-gold" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Workshop Leaders</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-2">
                List your workshops where photographers are planning their shoots. $200/year for unlimited listings, and your subscription includes all Premium features.
              </p>
              <p className="text-xs text-gold font-medium mb-4">
                Premium access included with Workshop Leader subscription
              </p>
              <span className="text-sm font-semibold text-gold group-hover:text-gold-dark transition-colors inline-flex items-center gap-1.5">
                List Your Workshop <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl text-white mb-3 tracking-[-0.02em]">
              Start planning better nights
            </h2>
            <p className="text-white/60 mb-8 max-w-md mx-auto">
              All three planners, unlimited locations, and full exports — one annual price.
            </p>
            {ctaAction ? (
              <Button variant="premium" onClick={ctaAction} size="lg" className="px-10">
                <Crown className="w-4 h-4 mr-2" />
                Get Premium — $25/year
              </Button>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-400">
                <Check className="w-4 h-4" />
                You have Premium Access
              </span>
            )}
            <p className="text-xs text-white/40 mt-4">
              One-time annual payment. Cancel anytime.
            </p>
          </div>
        </section>

      </div>
    </>
  );
};

export default Features;
